import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'

import { auth } from '../firebase-config'

const ANALYTICS_OVERVIEW_URL = 'https://australia-southeast1-upskillinstitute-au.cloudfunctions.net/analyticsOverview'
const ANALYTICS_DEBUG_URL = 'https://australia-southeast1-upskillinstitute-au.cloudfunctions.net/analyticsDebugRows'

const rangeOptions = [7, 30, 90]
const sectionOptions = [
  { id: 'overview', label: 'Overview' },
  { id: 'paid', label: 'Paid Media' },
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'traffic', label: 'Traffic' },
  { id: 'events', label: 'Events' },
  { id: 'debug', label: 'Debug' },
]

const currency = (value) => new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0,
}).format(Number(value) || 0)

const number = (value) => new Intl.NumberFormat('en-AU', {
  maximumFractionDigits: 1,
}).format(Number(value) || 0)

const percent = (value) => `${(Number(value) || 0).toFixed(1)}%`

const renderTable = ({ title, subtitle, columns, rows, emptyMessage = 'No rows yet.' }) => (
  <article className="analyticsPanel">
    <div className="analyticsPanelHeader">
      <h2>{title}</h2>
      <span>{subtitle}</span>
    </div>
    <div className="analyticsTableWrap">
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="analyticsEmptyTableCell">{emptyMessage}</td>
            </tr>
          ) : rows.map((row, index) => (
            <tr key={row.id || `${title}-${index}`}>
              {columns.map((column) => (
                <td key={column.key}>{column.render ? column.render(row) : row[column.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </article>
)

const AnalyticsDashboard = () => {
  const [days, setDays] = useState(30)
  const [section, setSection] = useState('overview')
  const [payload, setPayload] = useState(null)
  const [debugRows, setDebugRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const loadDashboard = useCallback(async (selectedDays = days) => {
    try {
      setLoading(true)
      setError('')

      const user = auth.currentUser
      if (!user) {
        throw new Error('Authentication required')
      }

      const token = await user.getIdToken()
      const headers = {
        Authorization: `Bearer ${token}`,
      }

      const [overviewResponse, debugResponse] = await Promise.all([
        axios.get(ANALYTICS_OVERVIEW_URL, {
          params: { days: selectedDays },
          headers,
        }),
        axios.get(ANALYTICS_DEBUG_URL, {
          params: { limit: 15 },
          headers,
        }),
      ])

      setPayload(overviewResponse.data || null)
      setDebugRows(debugResponse.data?.rows || [])
    } catch (nextError) {
      setError(nextError?.response?.data?.error || nextError.message || 'Unable to load analytics dashboard')
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => {
    loadDashboard(days)
  }, [days, loadDashboard])

  const summaryCards = useMemo(() => {
    const overview = payload?.overview || {}
    const spend = Number(overview.spend) || 0
    const conversions = Number(overview.conversions) || 0
    const platformConversions = Number(overview.platformConversions) || 0
    const revenue = Number(overview.revenue) || 0
    const platformRevenue = Number(overview.platformRevenue) || 0
    const sessions = Number(overview.sessions) || 0
    const users = Number(overview.users) || 0
    const eventCount = Number(overview.eventCount) || 0

    return [
      {
        label: 'Spend',
        value: currency(spend),
        detail: payload?.rangeStart ? `Reporting from ${payload.rangeStart}` : 'Live Firestore aggregate',
      },
      {
        label: 'Site conv.',
        value: number(conversions),
        detail: conversions > 0
          ? `${currency(Number(overview.costPerConversion) || 0)} cost / conversion`
          : 'No GA4 site conversions in range',
      },
      {
        label: 'Ad conv.',
        value: number(platformConversions),
        detail: 'Native Google Ads + Meta conversions',
      },
      {
        label: 'Site revenue',
        value: currency(revenue),
        detail: 'GA4 purchase revenue',
      },
      {
        label: 'Ad revenue',
        value: currency(platformRevenue),
        detail: 'Native platform conversion value',
      },
      {
        label: 'Sessions',
        value: number(sessions),
        detail: users > 0 ? `${number(users)} users` : 'GA4 access pending if zero',
      },
      {
        label: 'Events',
        value: number(eventCount),
        detail: 'GA4 tracked event volume',
      },
    ]
  }, [payload])

  const trendMax = useMemo(() => {
    const spends = (payload?.trends || []).map((row) => Number(row.spend) || 0)
    return Math.max(...spends, 1)
  }, [payload])

  return (
    <div className="analyticsPage">
      <section className="analyticsHero">
        <div className="analyticsHeroContent">
          <div className="analyticsEyebrow">Upskill Analytics</div>
          <h1>Marketing dashboard</h1>
          <p>
            Raw source data lands in Firestore daily from Google Ads, Meta, and GA4. The dashboard reads
            normalized reporting collections for spend, traffic, campaigns, landing pages, events, and
            conversion trends.
          </p>
        </div>
        <div className="analyticsHeroActions">
          <div className="analyticsRangePicker">
            {rangeOptions.map((option) => (
              <button
                key={option}
                type="button"
                className={days === option ? 'analyticsRangeButton active' : 'analyticsRangeButton'}
                onClick={() => setDays(option)}
              >
                {option}d
              </button>
            ))}
          </div>
          <button type="button" className="analyticsRefreshButton" onClick={() => loadDashboard(days)}>
            Refresh
          </button>
        </div>
      </section>

      <section className="analyticsStatusBar">
        <div>
          Daily sync: last <strong>{payload?.syncStrategy?.dailyLookbackDays || 2}</strong> days
        </div>
        <div>
          Nightly backfill: last <strong>{payload?.syncStrategy?.nightlyBackfillDays || 30}</strong> days
        </div>
        <div>
          Time zone: <strong>{payload?.syncStrategy?.scheduledTimeZone || 'Australia/Sydney'}</strong>
        </div>
      </section>

      <section className="analyticsSectionTabs">
        {sectionOptions.map((option) => (
          <button
            key={option.id}
            type="button"
            className={section === option.id ? 'analyticsSectionTab active' : 'analyticsSectionTab'}
            onClick={() => setSection(option.id)}
          >
            {option.label}
          </button>
        ))}
      </section>

      {error ? (
        <section className="analyticsMessage analyticsError">
          {error}
        </section>
      ) : null}

      {loading ? (
        <section className="analyticsMessage">Loading analytics data...</section>
      ) : null}

      {!loading && payload ? (
        <>
          <section className="analyticsCards">
            {summaryCards.map((card) => (
              <article key={card.label} className="analyticsCard">
                <div className="analyticsCardLabel">{card.label}</div>
                <div className="analyticsCardValue">{card.value}</div>
                <div className="analyticsCardDetail">{card.detail}</div>
              </article>
            ))}
          </section>

          {section === 'overview' ? (
            <>
              <section className="analyticsGrid">
                {renderTable({
                  title: 'Platform breakdown',
                  subtitle: `${payload.platforms?.length || 0} platforms`,
                  columns: [
                    { key: 'platform', label: 'Platform' },
                    { key: 'spend', label: 'Spend', render: (row) => currency(row.spend) },
                    { key: 'clicks', label: 'Clicks', render: (row) => number(row.clicks) },
                    { key: 'impressions', label: 'Impr.', render: (row) => number(row.impressions) },
                    { key: 'ctr', label: 'CTR', render: (row) => percent(row.ctr) },
                    { key: 'conversions', label: 'Conversions', render: (row) => number(row.conversions) },
                    { key: 'revenue', label: 'Revenue', render: (row) => currency(row.revenue) },
                  ],
                  rows: payload.platforms || [],
                })}

                <article className="analyticsPanel">
                  <div className="analyticsPanelHeader">
                    <h2>Spend trend</h2>
                    <span>{days} day window</span>
                  </div>
                  <div className="analyticsTrendList">
                    {(payload.trends || []).length === 0 ? (
                      <div className="analyticsEmptyState">No trend rows synced yet.</div>
                    ) : (
                      (payload.trends || []).map((row) => (
                        <div key={row.date} className="analyticsTrendRow">
                          <div className="analyticsTrendDate">{row.date}</div>
                          <div className="analyticsTrendBarTrack">
                            <div
                              className="analyticsTrendBarFill"
                              style={{ width: `${((Number(row.spend) || 0) / trendMax) * 100}%` }}
                            />
                          </div>
                          <div className="analyticsTrendValue">{currency(row.spend)}</div>
                        </div>
                      ))
                    )}
                  </div>
                </article>
              </section>

              <section className="analyticsGrid">
                {renderTable({
                  title: 'Course breakdown',
                  subtitle: 'Site outcomes from GA4, native outcomes from ad platforms',
                  columns: [
                    { key: 'courseName', label: 'Course' },
                    { key: 'spend', label: 'Spend', render: (row) => currency(row.spend) },
                    { key: 'siteConversions', label: 'Site conv.', render: (row) => number(row.siteConversions) },
                    { key: 'platformConversions', label: 'Ad conv.', render: (row) => number(row.platformConversions) },
                    { key: 'siteRevenue', label: 'Site revenue', render: (row) => currency(row.siteRevenue) },
                    { key: 'platformRevenue', label: 'Ad revenue', render: (row) => currency(row.platformRevenue) },
                    { key: 'roas', label: 'ROAS', render: (row) => `${Number(row.roas || 0).toFixed(2)}x` },
                  ],
                  rows: payload.courses || [],
                })}

                {renderTable({
                  title: 'Channel group performance',
                  subtitle: 'GA4 channel groups aligned to the standard traffic acquisition report',
                  columns: [
                    { key: 'defaultChannelGroup', label: 'Channel group' },
                    { key: 'sessions', label: 'Sessions', render: (row) => number(row.sessions) },
                    { key: 'users', label: 'Users', render: (row) => number(row.users) },
                    { key: 'conversions', label: 'Key events', render: (row) => number(row.conversions) },
                    { key: 'revenue', label: 'Revenue', render: (row) => currency(row.revenue) },
                  ],
                  rows: (payload.channelGroups || []).slice(0, 12),
                })}
              </section>
            </>
          ) : null}

          {section === 'paid' ? (
            <section className="analyticsStack">
              {renderTable({
                title: 'Paid media platforms',
                subtitle: 'Google Ads and Meta remain the source of truth for spend and click traffic',
                columns: [
                  { key: 'platform', label: 'Platform' },
                  { key: 'spend', label: 'Spend', render: (row) => currency(row.spend) },
                  { key: 'clicks', label: 'Clicks', render: (row) => number(row.clicks) },
                  { key: 'impressions', label: 'Impr.', render: (row) => number(row.impressions) },
                  { key: 'ctr', label: 'CTR', render: (row) => percent(row.ctr) },
                  { key: 'conversions', label: 'Native conv.', render: (row) => number(row.conversions) },
                  { key: 'revenue', label: 'Native revenue', render: (row) => currency(row.revenue) },
                  { key: 'roas', label: 'ROAS', render: (row) => `${Number(row.roas || 0).toFixed(2)}x` },
                ],
                rows: (payload.platforms || []).filter((row) => row.platform !== 'ga4'),
              })}
              {renderTable({
                title: 'Course spend allocation',
                subtitle: 'Spend vs GA4 site outcomes and native ad outcomes',
                columns: [
                  { key: 'courseName', label: 'Course' },
                  { key: 'spend', label: 'Spend', render: (row) => currency(row.spend) },
                  { key: 'siteConversions', label: 'Site conv.', render: (row) => number(row.siteConversions) },
                  { key: 'platformConversions', label: 'Ad conv.', render: (row) => number(row.platformConversions) },
                  { key: 'siteRevenue', label: 'Site revenue', render: (row) => currency(row.siteRevenue) },
                  { key: 'platformRevenue', label: 'Ad revenue', render: (row) => currency(row.platformRevenue) },
                  { key: 'roas', label: 'ROAS', render: (row) => `${Number(row.roas || 0).toFixed(2)}x` },
                ],
                rows: payload.courses || [],
              })}
            </section>
          ) : null}

          {section === 'campaigns' ? (
            <section className="analyticsStack">
              {renderTable({
                title: 'Campaign performance',
                subtitle: `${payload.campaigns?.length || 0} campaigns`,
                columns: [
                  { key: 'platform', label: 'Platform' },
                  { key: 'campaignName', label: 'Campaign' },
                  { key: 'channelName', label: 'Channel' },
                  { key: 'courseCode', label: 'Course' },
                  { key: 'spend', label: 'Spend', render: (row) => currency(row.spend) },
                  { key: 'clicks', label: 'Clicks', render: (row) => number(row.clicks) },
                  { key: 'sessions', label: 'Sessions', render: (row) => number(row.sessions) },
                  { key: 'conversions', label: 'Conv. (src)', render: (row) => number(row.conversions) },
                ],
                rows: payload.campaigns || [],
              })}
            </section>
          ) : null}

          {section === 'traffic' ? (
            <section className="analyticsStack">
              <section className="analyticsGrid">
                {renderTable({
                  title: 'Channel groups',
                  subtitle: 'GA4 traffic acquisition totals aligned to the default channel group report',
                  columns: [
                    { key: 'defaultChannelGroup', label: 'Channel group' },
                    { key: 'sessions', label: 'Sessions', render: (row) => number(row.sessions) },
                    { key: 'users', label: 'Users', render: (row) => number(row.users) },
                    { key: 'conversions', label: 'Key events', render: (row) => number(row.conversions) },
                    { key: 'revenue', label: 'Revenue', render: (row) => currency(row.revenue) },
                  ],
                  rows: payload.channelGroups || [],
                })}

                {renderTable({
                  title: 'Source / medium detail',
                  subtitle: 'GA4 detailed source and campaign drilldown',
                  columns: [
                    { key: 'defaultChannelGroup', label: 'Channel group' },
                    { key: 'sourceMedium', label: 'Source / Medium' },
                    { key: 'sessions', label: 'Sessions', render: (row) => number(row.sessions) },
                    { key: 'users', label: 'Users', render: (row) => number(row.users) },
                    { key: 'conversions', label: 'Key events', render: (row) => number(row.conversions) },
                    { key: 'revenue', label: 'Revenue', render: (row) => currency(row.revenue) },
                  ],
                  rows: payload.trafficSources || [],
                })}

                {renderTable({
                  title: 'Devices',
                  subtitle: 'GA4 device category mix',
                  columns: [
                    { key: 'deviceCategory', label: 'Device' },
                    { key: 'sessions', label: 'Sessions', render: (row) => number(row.sessions) },
                    { key: 'users', label: 'Users', render: (row) => number(row.users) },
                    { key: 'conversions', label: 'Conv.', render: (row) => number(row.conversions) },
                  ],
                  rows: payload.devices || [],
                })}
              </section>

              {renderTable({
                title: 'Landing pages',
                subtitle: 'GA4 landing pages with channel-group context',
                columns: [
                  { key: 'landingPage', label: 'Landing page' },
                  { key: 'defaultChannelGroup', label: 'Channel group' },
                  { key: 'sessions', label: 'Sessions', render: (row) => number(row.sessions) },
                  { key: 'users', label: 'Users', render: (row) => number(row.users) },
                  { key: 'conversions', label: 'Conv.', render: (row) => number(row.conversions) },
                ],
                rows: payload.landingPages || [],
              })}
            </section>
          ) : null}

          {section === 'events' ? (
            <section className="analyticsStack">
              {renderTable({
                title: 'Event breakdown',
                subtitle: 'Top tracked GA4 events',
                columns: [
                  { key: 'eventName', label: 'Event' },
                  { key: 'defaultChannelGroup', label: 'Channel group' },
                  { key: 'eventCount', label: 'Count', render: (row) => number(row.eventCount) },
                  { key: 'users', label: 'Users', render: (row) => number(row.users) },
                ],
                rows: payload.events || [],
              })}
            </section>
          ) : null}

          {section === 'debug' ? (
            <section className="analyticsStack">
              <article className="analyticsPanel">
                <div className="analyticsPanelHeader">
                  <h2>Latest synced rows</h2>
                  <span>{debugRows.length} rows</span>
                </div>
                <div className="analyticsDebugList">
                  {debugRows.length === 0 ? (
                    <div className="analyticsEmptyState">No synced rows yet.</div>
                  ) : (
                    debugRows.map((row) => (
                      <div key={row.id} className="analyticsDebugRow">
                        <div className="analyticsDebugTop">
                          <strong>{row.courseCode || 'unknown'}</strong>
                          <span>{row.platform}</span>
                        </div>
                        <div className="analyticsDebugMeta">{row.date} · {row.channelKey}</div>
                        <div className="analyticsDebugStats">
                          <span>Spend {currency(row.spend)}</span>
                          <span>Clicks {number(row.clicks)}</span>
                          <span>Impr. {number(row.impressions)}</span>
                          <span>Conv. {number(row.conversions)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </article>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

export default AnalyticsDashboard
